/**
 * /board/map — the board map in ITS OWN WINDOW (BOARD-MAP-STATES, Seth
 * 2026-09-29: "can it be dragged anywhere on the screen or to another
 * screen?"). Chrome-free like /team/tv: drag this window to the second
 * monitor and the board goes full-width columns next door.
 *
 * It renders nothing of its own — the board window streams rows + filters
 * over a BroadcastChannel ("pegasus-board-map") and takes selections back,
 * so both windows stay one brain: click a pin here, the call opens on the
 * board; change the date there, the pins move here. Opened cold (no board
 * window), it says so instead of guessing.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Map as MapIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { BoardMapView, type BoardMode, type ViewMode } from "./BoardView";
import { BoardConflictsProvider, useBoardConflicts } from "./conflicts";
import type { Dispatch } from "@/lib/types";
import type { PendingTicket } from "./queue";

interface Ctx {
  date: string;
  boardMode: BoardMode;
  viewMode: ViewMode;
  techFilter: string | null;
  rows: Dispatch[];
  selectedKey: string | null;
}

const prettyDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

export function BoardMapWindow() {
  const [sp] = useSearchParams();
  const [ctx, setCtx] = useState<Ctx>({
    date: sp.get("date") ?? new Date().toISOString().slice(0, 10),
    boardMode: "service",
    viewMode: "active",
    techFilter: null,
    rows: [],
    selectedKey: null,
  });
  const [linked, setLinked] = useState(false);
  const [selected, setSelected] = useState<Dispatch | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<PendingTicket | null>(null);
  const ch = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    document.title = "Pegasus — Board Map";
    const c = new BroadcastChannel("pegasus-board-map");
    ch.current = c;
    c.onmessage = (e) => {
      const m = e.data as ({ type?: string } & Partial<Ctx>) | null;
      if (m?.type !== "ctx") return;
      setLinked(true);
      setCtx((cur) => ({
        date: m.date ?? cur.date,
        boardMode: (m.boardMode as BoardMode) ?? cur.boardMode,
        viewMode: (m.viewMode as ViewMode) ?? cur.viewMode,
        techFilter: m.techFilter ?? null,
        rows: (m.rows as Dispatch[]) ?? cur.rows,
        selectedKey: (m.selectedKey as string | null) ?? null,
      }));
    };
    c.postMessage({ type: "hello" });
    // Say hello again shortly in case the board was mid-render the first time.
    const t = window.setTimeout(() => ch.current?.postMessage({ type: "hello" }), 1200);
    return () => {
      window.clearTimeout(t);
      c.close();
      ch.current = null;
    };
  }, []);

  // Mirror the board's selection into this window's highlight.
  useEffect(() => {
    setSelected(ctx.selectedKey ? ctx.rows.find((r) => r.dispatch === ctx.selectedKey) ?? null : null);
  }, [ctx.selectedKey, ctx.rows]);

  const post = (msg: unknown) => ch.current?.postMessage(msg);
  const onSelect = (d: Dispatch | null) => {
    setSelected(d);
    if (d) setSelectedTicket(null);
    post({ type: "select", row: d });
  };
  const onSelectTicket = (t: PendingTicket | null) => {
    setSelectedTicket(t);
    if (t) setSelected(null);
    post({ type: "selectTicket", ticket: t });
  };
  const onToggleTech = (empno: string) => post({ type: "toggleTech", empno });

  const conflicts = useBoardConflicts(ctx.rows, []);

  return (
    <div className="flex h-screen flex-col bg-slate-50" data-dense>
      <header className="flex items-center gap-2 border-b border-hairline bg-white px-4 py-2">
        <MapIcon className="h-4 w-4 text-brand-600" />
        <span className="text-[14px] font-bold text-slate-800">Board Map</span>
        <span className="text-[12.5px] font-semibold text-slate-400">{prettyDate(ctx.date)}</span>
        <span
          className={cn(
            "ml-auto inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-bold",
            linked ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700",
          )}
        >
          <span className={cn("h-1.5 w-1.5 rounded-full", linked ? "bg-emerald-500" : "bg-amber-500")} />
          {linked ? "Live with the board" : "Waiting for the board…"}
        </span>
      </header>
      <div className="min-h-0 flex-1 p-3">
        {!linked && ctx.rows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-dashed border-hairline bg-white text-center">
            <MapIcon className="h-8 w-8 text-slate-300" />
            <p className="mt-2 max-w-sm text-sm text-slate-500">
              This window mirrors the board. Open Pegasus's Board in another window and set its map
              to <span className="font-semibold text-slate-700">Window</span> — the day's stops will
              appear here.
            </p>
          </div>
        ) : (
          <div className="h-full">
            <BoardConflictsProvider conflicts={conflicts}>
              <BoardMapView
                boardMode={ctx.boardMode}
                viewMode={ctx.viewMode}
                rows={ctx.rows}
                date={ctx.date}
                techFilter={ctx.techFilter}
                selected={selected}
                selectedTicket={selectedTicket}
                onSelect={onSelect}
                onSelectTicket={onSelectTicket}
                onToggleTech={onToggleTech}
              />
            </BoardConflictsProvider>
          </div>
        )}
      </div>
    </div>
  );
}
